import React, { createContext, useContext, useState } from 'react'
import { createStore, useStore } from 'zustand'
import type { StoreApi } from 'zustand'
import { HostConfig } from 'adaptivecards'
import createAdaptiveCardHostConfig from '../../utils/createAdaptiveCardHostConfig'

interface Choice {
  title: string
  value: string
}

interface AdaptiveCardState {
  adaptiveCardHostConfig: any
  restoreInputsState: boolean
  values: Map<string, string | number | Array<string> | Array<number> | Choice | boolean>
  currentId: string | undefined
  currentToggleIds: Array<string>
  currentValue: Record<string, string | number | Array<string> | Array<number> | Choice | boolean> | undefined
  actions: {
    setAdaptiveCardHostConfig: (primary: string, background: string) => void
    setValue: (id: string, value: string | number | Array<string> | Array<number> | Choice | boolean) => void
    setToggleValue: (id: string, value: boolean) => void
    setCurrentId: (value: string | undefined) => void
    addCurrentToggleId: (id: string, defaultValue: boolean) => void
    setRestoreInputsState: (value: boolean) => void
    resetAdaptiveCardStore: () => void
  }
}

const initialState = {
  adaptiveCardHostConfig: {},
  restoreInputsState: false,
  values: new Map(),
  currentId: undefined,
  currentToggleIds: [],
  currentValue: undefined
}

const createLocaleStore = () =>
  createStore<AdaptiveCardState>((set) => {
    return {
      ...initialState,
      actions: {
        setAdaptiveCardHostConfig: (primary: string, background: string): void => {
          const customAdaptiveCardHostConfig = createAdaptiveCardHostConfig(primary, background)
          set({
            adaptiveCardHostConfig: new HostConfig(customAdaptiveCardHostConfig)
          })
        },
        setValue: (id: string, value: string | number | Array<string> | Array<number> | Choice | boolean): void => {
          set((state) => {
            if (id === state.currentId) {
              const currentValues = state.values
              state.restoreInputsState && currentValues.set(id, value)

              return {
                values: currentValues,
                currentValue: { [id]: value }
              }
            }

            return state
          })
        },
        setToggleValue: (id: string, value: boolean): void => {
          set((state) => {
            if (state.currentToggleIds.includes(id)) {
              const currentValues = state.values
              state.restoreInputsState && currentValues.set(id, value)

              const tempCurrentValue = state.currentValue || {}
              state.currentToggleIds.forEach((identifier) => {
                if (identifier === id) {
                  tempCurrentValue[identifier] = value
                } else {
                  const tempValueUntyped = state.currentValue?.[identifier] || false
                  tempCurrentValue[identifier] = Array.isArray(tempValueUntyped) ? false : (tempValueUntyped as boolean)
                }
              })

              return {
                values: currentValues,
                currentValue: tempCurrentValue
              }
            }

            return state
          })
        },
        setCurrentId: (value: string | undefined): void => {
          set({
            currentId: value,
            currentValue: undefined,
            currentToggleIds: []
          })
        },
        addCurrentToggleId: (id: string, defaultValue: boolean): void => {
          set((state) => {
            const currentValues = state.values
            const currentToggleIds = state.currentToggleIds
            currentValues.set(id, defaultValue)

            return {
              values: currentValues,
              currentToggleIds: [...currentToggleIds, id]
            }
          })
        },
        setRestoreInputsState: (value: boolean): void => {
          set({ restoreInputsState: value })
        },
        resetAdaptiveCardStore: () => {
          set({
            ...initialState
          })
        }
      }
    }
  })

const AdaptiveCardContext = createContext<StoreApi<AdaptiveCardState> | null>(null)

const useAdaptiveCardStore = <T,>(selector: (state: AdaptiveCardState) => T): T => {
  const store = useContext(AdaptiveCardContext)
  if (!store) throw new Error('Retorik Framework > useAdaptiveCardStore must be used within AdaptiveCardProvider')
  return useStore(store, selector)
}

const AdaptiveCardProvider = ({ children }) => {
  const [store] = useState(() => createLocaleStore())

  return <AdaptiveCardContext.Provider value={store}>{children}</AdaptiveCardContext.Provider>
}

export default AdaptiveCardProvider
export { useAdaptiveCardStore }
export type { Choice }
